import {
  InviteDeliveryMethod,
  InviteStatus,
  OnboardingSource,
  OnboardingStatus,
} from '@ciimar/shared';
import { useI18n, useT } from '../lib/i18n';

/**
 * Status badge helpers. The CSS classes are language-independent; the human
 * labels are resolved through the i18n layer, so each is exposed as a small
 * hook returning the translated record (`useStatusLabels()[status]`).
 */

export const STATUS_CLASS: Record<OnboardingStatus, string> = {
  [OnboardingStatus.PendingReview]: 'badge pending',
  [OnboardingStatus.ChangesRequested]: 'badge changes',
  [OnboardingStatus.Approved]: 'badge approved',
  [OnboardingStatus.LaunchedInSistrade]: 'badge synced',
  [OnboardingStatus.Rejected]: 'badge rejected',
};

export const INVITE_STATUS_CLASS: Record<InviteStatus, string> = {
  [InviteStatus.Pending]: 'badge pending',
  [InviteStatus.Submitted]: 'badge approved',
  [InviteStatus.Cancelled]: 'badge rejected',
  [InviteStatus.Expired]: 'badge changes',
};

export function useStatusLabels(): Record<OnboardingStatus, string> {
  const t = useT();
  return {
    [OnboardingStatus.PendingReview]: t('status.pendingReview'),
    [OnboardingStatus.ChangesRequested]: t('status.changesRequested'),
    [OnboardingStatus.Approved]: t('status.approved'),
    [OnboardingStatus.LaunchedInSistrade]: t('status.launchedInSistrade'),
    [OnboardingStatus.Rejected]: t('status.rejected'),
  };
}

export function useSourceLabels(): Record<OnboardingSource, string> {
  const t = useT();
  return {
    [OnboardingSource.Manager]: t('status.source.manager'),
    [OnboardingSource.SelfRegistration]: t('status.source.selfRegistration'),
  };
}

export function useInviteStatusLabels(): Record<InviteStatus, string> {
  const t = useT();
  return {
    [InviteStatus.Pending]: t('status.invite.pending'),
    [InviteStatus.Submitted]: t('status.invite.submitted'),
    [InviteStatus.Cancelled]: t('status.invite.cancelled'),
    [InviteStatus.Expired]: t('status.invite.expired'),
  };
}

export function useDeliveryLabels(): Record<InviteDeliveryMethod, string> {
  const t = useT();
  return {
    [InviteDeliveryMethod.Email]: t('status.delivery.email'),
    [InviteDeliveryMethod.Link]: t('status.delivery.link'),
    [InviteDeliveryMethod.Code]: t('status.delivery.code'),
  };
}

/** Localized date-time; follows the active language (pt-PT / en-GB). */
export function useFormatDate(): (iso?: string | null) => string {
  const { lang } = useI18n();
  const locale = lang === 'pt' ? 'pt-PT' : 'en-GB';
  return (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(locale);
  };
}

/** Non-hook date-time formatter (pt-PT), for contexts without hook access. */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-PT');
}
